/**
 * Tree View Component
 *
 * Displays tasks in a hierarchical dependency structure matching CLI `hence board --tree` format.
 */

import { useMemo } from "react";
import type { Task } from "../lib/usePlanReasoning";
import Avatar from "./Avatar";

interface TreeViewProps {
  tasks: Task[];
  taskDeps: Map<string, string[]>;
  isLoading?: boolean;
  onTaskClick?: (taskId: string) => void;
  selectedTask?: string | null;
}

// Status icons matching CLI output
const STATUS_ICONS: Record<string, string> = {
  done: "✓",
  ready: "●",
  in_progress: "▶",
  blocked: "✗",
  backlog: "○",
};

// Status colors for styling
const STATUS_COLORS: Record<string, string> = {
  done: "text-success",
  ready: "text-primary",
  in_progress: "text-secondary",
  blocked: "text-warning",
  backlog: "text-base-content/50",
};

interface TreeNode {
  taskId: string;
  children: TreeNode[];
  isBackref: boolean;
}

export default function TreeView({
  tasks,
  taskDeps,
  isLoading,
  onTaskClick,
  selectedTask,
}: TreeViewProps) {
  const taskMap = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);

  // Build dependents map (invert taskDeps)
  // dependents[A] = tasks that depend on A (i.e., A is a dependency of these tasks)
  const dependents = useMemo(() => {
    const result = new Map<string, string[]>();
    for (const task of tasks) {
      result.set(task.id, []);
    }
    for (const [taskId, deps] of taskDeps) {
      for (const dep of deps) {
        if (result.has(dep)) {
          result.get(dep)!.push(taskId);
        }
      }
    }
    return result;
  }, [tasks, taskDeps]);

  // Find root tasks: tasks with no incomplete dependencies (done deps or no deps)
  const rootTasks = useMemo(() => {
    return tasks.filter((task) => {
      const deps = taskDeps.get(task.id) || [];
      // A task is a root if all its dependencies are done or it has no dependencies
      return deps.every((depId) => {
        const depTask = taskMap.get(depId);
        return !depTask || depTask.status === "done";
      });
    });
  }, [tasks, taskDeps, taskMap]);

  // Build tree recursively, tracking printed tasks to detect cycles
  const buildTree = useMemo(() => {
    const printed = new Set<string>();

    function buildSubtree(taskId: string): TreeNode {
      if (printed.has(taskId)) {
        return { taskId, children: [], isBackref: true };
      }
      printed.add(taskId);

      const children = (dependents.get(taskId) || [])
        .filter((childId) => taskMap.has(childId))
        .sort((a, b) => a.localeCompare(b))
        .map((childId) => buildSubtree(childId));

      return { taskId, children, isBackref: false };
    }

    // Sort root tasks alphabetically
    const sortedRoots = [...rootTasks].sort((a, b) => a.id.localeCompare(b.id));
    return sortedRoots.map((task) => buildSubtree(task.id));
  }, [rootTasks, dependents, taskMap]);

  // Pre-compute tree lines
  interface TreeLine {
    taskId: string;
    prefix: string;
    isBackref: boolean;
  }

  const treeData = useMemo(() => {
    const lines: TreeLine[] = [];

    function traverse(
      node: TreeNode,
      prefix: string,
      isLast: boolean,
      depth: number
    ) {
      const branch = depth === 0 ? "" : isLast ? "└── " : "├── ";
      lines.push({
        taskId: node.taskId,
        prefix: prefix + branch,
        isBackref: node.isBackref,
      });

      if (!node.isBackref) {
        const childPrefix = prefix + (depth === 0 ? "" : isLast ? "    " : "│   ");
        node.children.forEach((child, index) => {
          const childIsLast = index === node.children.length - 1;
          traverse(child, childPrefix, childIsLast, depth + 1);
        });
      }
    }

    for (let i = 0; i < buildTree.length; i++) {
      traverse(buildTree[i], "", i === buildTree.length - 1, 0);
    }

    return lines;
  }, [buildTree]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Legend */}
      <div className="flex items-center gap-2 sm:gap-4 px-2 sm:px-4 py-1.5 sm:py-2 bg-base-100 border-b border-base-300 flex-wrap font-mono text-[10px] sm:text-sm">
        <span className="text-base-content/50">Legend:</span>
        <span className="text-success">✓ done</span>
        <span className="text-primary">● ready</span>
        <span className="text-secondary">▶ in-progress</span>
        <span className="text-warning">✗ blocked</span>
        <span className="text-base-content/50">○ backlog</span>
      </div>

      {/* Tree content */}
      <div className="flex-1 overflow-auto bg-base-200 p-4 font-mono text-sm">
        {tasks.length === 0 ? (
          <div className="text-base-content/40 italic">No tasks to display</div>
        ) : treeData.length === 0 ? (
          <div className="text-base-content/40 italic">
            No tree structure found ({tasks.length} tasks)
          </div>
        ) : (
          treeData.map((line, index) => {
            const task = taskMap.get(line.taskId);
            if (!task) return null;

            const icon = STATUS_ICONS[task.status] || "○";
            const colorClass = STATUS_COLORS[task.status] || STATUS_COLORS.backlog;
            const isSelected = selectedTask === task.id;

            const borderColor = {
              done: "border-l-success",
              ready: "border-l-primary",
              in_progress: "border-l-secondary",
              blocked: "border-l-warning",
              backlog: "border-l-base-300",
            }[task.status] || "border-l-base-300";

            return (
              <div
                key={index}
                className="flex items-center py-1"
              >
                <span className="text-base-content/50 select-none whitespace-pre">
                  {line.prefix}
                </span>
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 bg-base-100 border border-base-300 border-l-4 ${borderColor} rounded cursor-pointer hover:shadow-sm transition-all min-w-[180px] ${
                    isSelected ? "ring-2 ring-primary ring-offset-1" : ""
                  }`}
                  onClick={() => onTaskClick?.(task.id)}
                >
                  <span className={`${colorClass}`}>{icon}</span>
                  <span className={`font-medium ${isSelected ? "text-primary" : ""}`}>
                    {task.id}
                  </span>
                  {task.agent && task.status === "in_progress" && (
                    <Avatar name={task.agent} keyHash={task.agentKeyHash} size="xs" />
                  )}
                  {line.isBackref && (
                    <span className="text-base-content/40 text-xs italic">(→)</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
}
